import { useEffect, useRef } from "react";

interface VturbPlayerProps {
  videoId: string;
  scriptUrl: string;
  className?: string;
}

// Declarar tipo global para vturb-smartplayer
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "vturb-smartplayer": {
        id?: string;
        style?: React.CSSProperties;
        children?: React.ReactNode;
      } & React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

// Track de scripts carregados globalmente
const scriptLoadedTracking = new Set<string>();

/**
 * Componente reutilizável para renderizar vídeos do vturb
 * Carrega o script do vturb dinamicamente e renderiza o player
 */
export const VturbPlayer = ({
  videoId,
  scriptUrl,
  className = "w-full",
}: VturbPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerLoadedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || playerLoadedRef.current) return;

    const loadVturbPlayer = async () => {
      // Limpar container
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }

      // Criar elemento do player
      const playerWrapper = document.createElement("div");
      playerWrapper.style.width = "100%";
      playerWrapper.style.display = "block";

      const playerElement = document.createElement("vturb-smartplayer") as any;
      playerElement.id = videoId;

      // Deixar vturb controlar seu próprio tamanho
      playerElement.style.display = "block";
      playerElement.style.width = "100%";
      playerElement.style.maxWidth = "100%";
      playerElement.style.margin = "0 auto";

      playerWrapper.appendChild(playerElement);

      if (containerRef.current) {
        // garante que o container seja “pai” do absolute
        containerRef.current.style.position = "relative";
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(playerWrapper);
      }

      // Carregar script se ainda não foi carregado
      if (!scriptLoadedTracking.has(scriptUrl)) {
        scriptLoadedTracking.add(scriptUrl);

        // Aguardar o script ser carregado
        return new Promise((resolve) => {
          if (document.querySelector(`script[src="${scriptUrl}"]`)) {
            // Script já está no DOM, apenas resolver
            resolve(null);
            return;
          }

          const script = document.createElement("script");
          script.src = scriptUrl;
          script.async = true;
          script.type = "text/javascript";

          script.onload = () => {
            playerLoadedRef.current = true;
            // Forçar renderização do vturb
            setTimeout(() => {
              if ((window as any).vTurbLoadPlayer) {
                (window as any).vTurbLoadPlayer();
              }
            }, 100);
            resolve(null);
          };

          script.onerror = () => {
            console.error(`Erro ao carregar vturb script: ${scriptUrl}`);
            resolve(null);
          };

          document.head.appendChild(script);
        });
      } else {
        // Script já foi carregado
        playerLoadedRef.current = true;
        setTimeout(() => {
          if ((window as any).vTurbLoadPlayer) {
            (window as any).vTurbLoadPlayer();
          }
        }, 100);
      }
    };

    loadVturbPlayer();
  }, [videoId, scriptUrl]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        display: "block",
      }}
    >
      {/* O elemento vturb-smartplayer será inserido aqui via useEffect */}
    </div>
  );
};
